import React, { useEffect, useRef, useState } from "react";
import styles from "./FacilityComparison.module.scss";
import { SonicRelatedFacilityData } from "../../common/classes/SonicRelatedFacility";
import {
  getDefaultImage,
  parsePrefecture,
  sendEventTracking,
} from "../../utils/GeneralUtils";
import { traitsToDisplayNames } from "../../utils/FacilityState";
import { genPriceStatusName } from "../../utils/PriceStatus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  facilities: Array<SonicRelatedFacilityData>;
  source: string;
};
const FacilityComparison: React.FC<Props> = ({ facilities, source }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const genSeparatedTraitsArea = (traits: Array<string>) => {
    const array_1: Array<string> = [];
    const array_2: Array<string> = [];

    traitsToDisplayNames(traits).map((t, i) => {
      if (i < 3) {
        array_1.push(t);
      } else {
        array_2.push(t);
      }
    });

    return (
      <>
        <div className={styles["traits"]}>
          {array_1.map((value, i) => (
            <span key={i}>{value}</span>
          ))}
        </div>
        <div className={`${styles["traits"]} ${styles["second-area"]}`}>
          {array_2.map((value, i) => (
            <span key={i}>{value}</span>
          ))}
        </div>
      </>
    );
  };

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 画面幅が768px以上のみホイールイベントを追加する。
    if (!window.matchMedia("(min-width: 768px)").matches) return;

    // TODO 画面のリサイズに対応するためスクロールごとに最新のRef.currentを変数に格納しています、より良い手段があれば修正すべき。
    const onWheel = (e: any) => {
      if (!tableRef.current) return;

      const currentRef = tableRef.current;

      // ホイールの処理を元に戻す。（右スクロールから縦スクロールに）
      if (
        Math.ceil(currentRef.scrollLeft) >=
          currentRef.scrollWidth - currentRef.clientWidth &&
        e.deltaY > 0
      ) {
        return;
      }

      // ホイールの処理を元に戻す。（左スクロールから縦スクロールに）
      if (currentRef.scrollLeft <= 0 && e.deltaY < 0) {
        return;
      }

      // ホイールの処理をキャンセルし、横にスクロールさせる。
      e.preventDefault();
      currentRef.scrollTo({
        left: currentRef.scrollLeft + e.deltaY,
        behavior: "smooth",
      });
    };

    // 比較表のホイールイベントをセット。
    const el = tableRef.current;

    if (el) {
      el.addEventListener("wheel", onWheel);

      return () => {
        el.removeEventListener("wheel", onWheel);
      };
    }
  }, []);

  return (
    <div className={styles["facility-comparison-wrapper"]}>
      <h2 className={styles["facility-comparison-title"]}>
        他の方はこんな施設も見ています
      </h2>

      <div className={styles["facility-comparison"]} ref={tableRef}>
        <div className={styles["field-name"]}>基本情報</div>
        <div className={styles["field-name"]}>料金</div>
        <div className={styles["field-name"]}>収容人数</div>
        <div className={styles["field-name"]}>特徴</div>
        <div className={styles["field-name"]}>SELeCTレビュー</div>

        {facilities.map((f) => (
          <React.Fragment key={f.id}>
            <div className={styles["basic-info"]}>
              <div className={styles["basic-info__prefecture"]}>
                {f.prefecture
                  ? parsePrefecture(f.prefecture).toLocaleUpperCase()
                  : "未設定"}
              </div>
              <div className={styles["basic-info__mainImg"]}>
                <img src={f.imgs ? f.imgs[0].medium.url : getDefaultImage()} />
              </div>
              <div className={styles["basic-info__name"]}>
                {f.name !== source ? (
                  <SonicLink
                    to={`/facility/${f.id}`}
                    onClick={() =>
                      sendEventTracking("click", "facilityComparison", f.name)
                    }
                  >
                    {f.name}
                  </SonicLink>
                ) : (
                  <span>{f.name}</span>
                )}
              </div>
            </div>
            <div className={styles["price-status"]}>
              {genPriceStatusName(f.price_status)}
            </div>
            <div className={styles["seating-capacity"]}>
              <span>
                {f.seating_capacity_text
                  ? f.seating_capacity_text
                  : f.seating_capacity_max
                  ? `${f.seating_capacity_max} 人`
                  : "－"}
              </span>
            </div>
            <div
              className={
                isOpen
                  ? styles["traits-wrapper"]
                  : `${styles["traits-wrapper"]} ${styles["hide"]}`
              }
            >
              {genSeparatedTraitsArea(f.traits)}
              {f.traits.length > 3 ? (
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className={styles["traits-toggle"]}
                >
                  {isOpen ? (
                    <FontAwesomeIcon
                      icon={["fal", "chevron-down"]}
                      rotation={180}
                    />
                  ) : (
                    <FontAwesomeIcon icon={["fal", "chevron-down"]} />
                  )}
                </button>
              ) : null}
            </div>
            <div className={styles["select-review"]}>{f.select_review}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default FacilityComparison;
